import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { FieldProps } from 'formik'
import { ComponentProps, ReactNode } from 'react'

import { TPropsWithFieldProps } from '~/components/formikFields/models'
import WrapWithFastField from '~/components/formikFields/wrapWithFastField'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import rem from '~/utils/ui'

type TLabelWrapperProps = Pick<
  TProps,
  'label' | 'required' | 'withInternalLabel' | 'labelEndAdornment'
> & {
  labelId: string
}

function LabelWrapper({
  label,
  labelId,
  labelEndAdornment,
  required,
  withInternalLabel,
}: TLabelWrapperProps) {
  const theme = useTheme()

  if (!label) return null

  if (withInternalLabel) {
    return (
      <InputLabel id={labelId} required={required}>
        {label}
      </InputLabel>
    )
  }

  return (
    <Stack alignItems="center" direction="row" spacing={1} useFlexGap>
      <FormLabel
        required={required}
        sx={{
          '&, &.Mui-focused': {
            color: theme.palette.mainFont,
            fontSize: rem(14),
            fontWeight: 500,
          },
        }}
      >
        {label}
      </FormLabel>
      {labelEndAdornment}
    </Stack>
  )
}

export type TFormikLabeledSelectOption = {
  label: string
  value: string
  disabled?: boolean
}

export type TRenderFormikLabeledSelectOption = (
  option: TFormikLabeledSelectOption,
) => ReactNode

type TSelectFieldProps = FieldProps<string, object>

type TProps = {
  capability?: ECapability
  disabled?: boolean
  helperText?: ReactNode
  label?: string
  labelEndAdornment?: ReactNode
  onChange?: (
    event: SelectChangeEvent<string>,
    child: ReactNode,
    fieldProps: TSelectFieldProps,
  ) => void
  options: TFormikLabeledSelectOption[]
  placeholder?: string
  renderOption?: TRenderFormikLabeledSelectOption
  required?: boolean
  sx?: SxProps<Theme>
  withInternalLabel?: boolean
} & TPropsWithFieldProps<string, object> &
  Pick<ComponentProps<typeof Select>, 'MenuProps'>

const FormikLabeledSelect = WrapWithFastField<TProps, string, object>(
  function Component({
    capability,
    disabled = false,
    fieldProps,
    helperText = null,
    label = undefined,
    labelEndAdornment = null,
    onChange: onChangeProps = undefined,
    options,
    placeholder = '',
    renderOption: renderOptionFromProps = undefined,
    required = false,
    sx = {},
    withInternalLabel = false,
    MenuProps = {},
  }) {
    const { canEdit } = useCapabilities()

    const { field, form, meta } = fieldProps
    const { isSubmitting } = form
    const { error, touched } = meta
    const { name, onChange, onBlur, value } = field

    const isDisabledByCapability = capability && !canEdit(capability)
    const isSelectDisabled = disabled || isSubmitting || isDisabledByCapability
    const labelId = `${name}-label`
    const handleChange = (
      event: SelectChangeEvent<string>,
      child: ReactNode,
    ) => {
      onChange(event)
      onChangeProps?.(event, child, fieldProps)
    }

    return (
      <FormControl
        error={!!touched && !!error}
        fullWidth
        sx={{ gap: rem(8), ...sx }}
      >
        <LabelWrapper
          label={label}
          labelId={labelId}
          labelEndAdornment={labelEndAdornment}
          required={required}
          withInternalLabel={withInternalLabel}
        />
        <Select
          displayEmpty
          disabled={isSelectDisabled}
          error={!!touched && !!error}
          label={withInternalLabel ? label : undefined}
          labelId={withInternalLabel ? labelId : undefined}
          MenuProps={{
            sx: {
              maxHeight: rem(350),
              '.MuiPaper-root': {
                borderRadius: rem(8),
                marginTop: rem(4),
              },
            },
            ...MenuProps,
          }}
          name={name}
          onBlur={onBlur}
          onChange={handleChange}
          renderValue={(selected) => {
            const selectedOptionLabel =
              options.find((option) => option.value === selected)?.label || ''

            if (
              (selected.length === 0 || !selectedOptionLabel) &&
              placeholder
            ) {
              return (
                <Typography
                  sx={{
                    color: 'currentcolor',
                    opacity: 0.42,
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {placeholder}
                </Typography>
              )
            }

            return selectedOptionLabel
          }}
          size={withInternalLabel ? 'medium' : 'small'}
          sx={{
            width: '100%',
            height: withInternalLabel ? undefined : rem(36),
            borderRadius: withInternalLabel ? rem(4) : rem(8),
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              ...(withInternalLabel ? {} : { paddingBlock: 0 }),
            },
          }}
          value={value || ''}
          variant="outlined"
        >
          {options.map((option, index) => (
            <MenuItem
              key={`${option.value}-${index.toString()}`}
              disabled={option.disabled}
              value={option.value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {renderOptionFromProps?.(option) ?? option.label}
            </MenuItem>
          ))}
        </Select>
        {(!!touched && !!error) || Boolean(helperText) ? (
          <FormHelperText error={!!touched && !!error}>
            {(touched && error) || helperText}
          </FormHelperText>
        ) : null}
      </FormControl>
    )
  },
)

export default FormikLabeledSelect
